/** Material Catalog — v1 materialsPanel/matCard, read-only Phase A: search +
 * category filter + the exact card (image box, 2-line name, category, price
 * w/ unit + Lower-Price pill / higher-trend mark, supplier • vendor, SKU,
 * View Product) + a read-only price-history popover. Write actions render
 * disabled with the Phase-B note. */
import { useEffect, useMemo, useState } from "react";
import {
  MAT_MSG, catName, matFilter, matPrice, vendorName, emptyMaterial, matId, matCatId, parseMatCsv,
  MAT_VENDORS, MAT_UNITS,
  type CatalogMaterial, type MaterialCat, type MaterialInput, type MaterialPriceResult,
} from "./api";
import { ConfirmDelete, useConfirmDelete } from "../common/ConfirmDelete";

export function MaterialsPage({
  rows,
  cats,
  ready,
  loadError,
  onRefresh,
  canEdit,
  onPutMaterial,
  onDelMaterial,
  onPutCat,
  onDelCat,
  onImportUrl,
}: {
  rows: CatalogMaterial[];
  cats: MaterialCat[];
  ready: boolean;
  /** the read failed — NOT the same as an empty catalog */
  loadError?: string;
  onRefresh: () => void;
  canEdit: boolean;
  onPutMaterial: (id: string, input: MaterialInput, keepHistory: { date: string; price: number }[]) => void;
  onDelMaterial: (natKey: string) => void;
  onPutCat: (id: string, name: string) => void;
  onDelCat: (id: string) => void;
  onImportUrl?: (url: string, onWake: () => void) => Promise<MaterialPriceResult>;
}) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [hist, setHist] = useState<CatalogMaterial | null>(null);
  const [broken, setBroken] = useState<Set<string>>(new Set());
  const [editMat, setEditMat] = useState<{ id: string; input: MaterialInput; history: { date: string; price: number }[] } | null>(null);
  const [showCats, setShowCats] = useState(false);
  const [showImport, setShowImport] = useState(false);
  /** phone ⋯ sheet holding the housekeeping actions (option 1) */
  const [moreOpen, setMoreOpen] = useState(false);
  const [delMat, setDelMat] = useState<CatalogMaterial | null>(null);

  const newMaterial = () => setEditMat({ id: "", input: emptyMaterial(cat !== "all" ? cat : null), history: [] });
  const editMaterial = (m: CatalogMaterial) => setEditMat({
    id: m.natKey,
    input: { name: m.name, categoryId: m.categoryId, price: m.price ?? 0, unit: m.unit ?? "each", supplier: m.supplier ?? "", vendor: m.vendor ?? "", sku: m.sku ?? "", internet: m.internet ?? "", notes: m.notes ?? "", website: m.website ?? "", thumbnail: m.thumbnail ?? "" },
    history: m.history,
  });

  useEffect(() => {
    onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = useMemo(() => matFilter(rows, cats, cat, q), [rows, cats, cat, q]);

  return (
    <div className="mat">
      <header className="pagehead">
        <div className="mathead">
          <div className="matbadge" aria-hidden="true">📦</div>
          <div>
            <h1>{MAT_MSG.title}</h1>
            <p className="matheadsub">{MAT_MSG.sub}</p>
            <span className="count">{rows.length} materials</span>
          </div>
        </div>
        <div className="matacts">
          {canEdit ? (
            <>
              {/* housekeeping actions — a ⋯ sheet on phones (option 1, Tom
                  2026-07-30), the flat row on desktop. Same grammar as the
                  SOW toolbar: one filled primary, the rest behind ⋯. */}
              <span className="matactsfull">
              <button className="btn" onClick={() => setShowCats(true)}>+ Add Category</button>
              <button className="btn" onClick={() => setShowImport(true)}>⬆ Import CSV</button>
              <button className="btn" disabled title="Bulk price refresh — coming with pricing sync">💲 Update Pricing</button>
              </span>
              <button className="btn primary" onClick={newMaterial}>+ Add Material</button>
              <button className="btn matmore" aria-label="More catalog actions" onClick={() => setMoreOpen(true)}>⋯</button>
            </>
          ) : (
            <>
              <span className="matactsfull">
              <button className="btn" disabled title={MAT_MSG.readOnly}>+ Add Category</button>
              <button className="btn" disabled title={MAT_MSG.readOnly}>⬆ Import CSV</button>
              <button className="btn" disabled title="Bulk price refresh — coming with pricing sync">💲 Update Pricing</button>
              </span>
              <button className="btn primary" disabled title={MAT_MSG.readOnly}>+ Add Material</button>
              <button className="btn matmore" aria-label="More catalog actions" onClick={() => setMoreOpen(true)}>⋯</button>
            </>
          )}
        </div>
      </header>
      {moreOpen ? (
        <div className="pdmscrim" onClick={() => setMoreOpen(false)}>
          <div className="pdmsheet" onClick={(e) => e.stopPropagation()}>
            <div className="pdmhd">Catalog</div>
            {canEdit ? (
              <>
                <button className="pdmitem" onClick={() => { setMoreOpen(false); setShowCats(true); }}>+ Add category</button>
                <button className="pdmitem" onClick={() => { setMoreOpen(false); setShowImport(true); }}>⬆ Import CSV</button>
              </>
            ) : (
              <div className="pdpinnote">{MAT_MSG.readOnly}</div>
            )}
            {/* the disabled desktop button becomes a row that says WHY —
                a grey button with a hover tooltip explains nothing on touch */}
            <div className="pdpinnote">💲 Update pricing — bulk price refresh arrives with pricing sync.</div>
          </div>
        </div>
      ) : null}
      {!canEdit ? <div className="matphaseb">{MAT_MSG.readOnly}</div> : null}
      {loadError ? (
        <div className="pill stale">
          ⚠️ Couldn't read the material catalog — {loadError}. This is a failed read,
          not an empty catalog; nothing has been lost.
          {" "}<button className="btn slim" onClick={onRefresh}>Try again</button>
        </div>
      ) : null}
      {!ready && !loadError ? <div className="pill stale">{MAT_MSG.notReady}</div> : null}

      <div className="matbar">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search materials..." />
        <select value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="all">All Categories</option>
          {cats.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="matgrid">
        {shown.length ? (
          shown.map((m) => (
            <div key={m.natKey} className="matcard">
              <div className="matimg">
                {m.thumbnail && !broken.has(m.natKey) ? (
                  <img
                    src={m.thumbnail}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    alt=""
                    onError={() => setBroken((b) => new Set(b).add(m.natKey))}
                  />
                ) : (
                  <span className="matnoimg">{m.thumbnail ? MAT_MSG.imgFail : MAT_MSG.noImage}</span>
                )}
              </div>
              <div className="matnamerow">
                <h3>{m.name}</h3>
                <div className="matcardacts">
                  {m.history.length ? (
                    <button className="mathistbtn" title="Price History" onClick={() => setHist(m)}>🕘</button>
                  ) : null}
                  <button className="mathistbtn cmp" title="Compare prices — coming with pricing sync" disabled>$</button>
                  {canEdit ? <button className="mathistbtn" title="Edit" onClick={() => editMaterial(m)}>✎</button> : null}
                  {canEdit ? <button className="mathistbtn del" title="Delete" onClick={() => setDelMat(m)}>✕</button> : null}
                </div>
              </div>
              <p className="matcat">{catName(cats, m.categoryId)}</p>
              {m.price !== null ? (
                <div className="matpricerow">
                  <span className="matprice">
                    {matPrice(m.price)}
                    {m.unit ? <span className="matunit"> / {m.unit}</span> : null}
                  </span>
                  {m.priceTrend === "lower" ? <span className="lbadge lower">Lower Price</span> : null}
                  {m.priceTrend === "higher" ? <span className="mathigher" title="Price went up">↗</span> : null}
                </div>
              ) : null}
              <div className="matmeta">
                {m.supplier ? <b>{m.supplier}</b> : null}
                {m.supplier && m.vendor ? <span className="dotsep">•</span> : null}
                {m.vendor ? <span>{vendorName(m.vendor)}</span> : null}
              </div>
              {m.sku ? <p className="matsku">SKU: {m.sku}</p> : null}
              {m.website ? (
                <a className="matlink" href={m.website} target="_blank" rel="noopener noreferrer">
                  View Product ↗
                </a>
              ) : null}
            </div>
          ))
        ) : (
          <div className="matempty">{MAT_MSG.empty}</div>
        )}
      </div>

      {hist ? (
        <div className="cmpmodalscrim" onClick={() => setHist(null)}>
          <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
            <div className="cmpmodalbody">
              <div className="cmpmodalhead">
                <h3>Price history — {hist.name.slice(0, 48)}</h3>
                <button className="close" onClick={() => setHist(null)}>×</button>
              </div>
              <table className="mathisttable">
                <thead>
                  <tr><th>Date</th><th className="r">Price then</th></tr>
                </thead>
                <tbody>
                  {[...hist.history].reverse().map((h, i) => (
                    <tr key={i}>
                      <td>{h.date || "—"}</td>
                      <td className="r">{matPrice(h.price)}</td>
                    </tr>
                  ))}
                  <tr className="cur">
                    <td>now</td>
                    <td className="r">{hist.price !== null ? matPrice(hist.price) : "—"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {editMat ? (
        <MaterialEditor
          init={editMat}
          cats={cats}
          onCancel={() => setEditMat(null)}
          onSave={(input) => {
            onPutMaterial(editMat.id || matId(), input, editMat.history);
            setEditMat(null);
          }}
          {...(onImportUrl ? { onImportUrl } : {})}
        />
      ) : null}

      {showCats ? (
        <CategoryManager cats={cats} rows={rows} onClose={() => setShowCats(false)} onAdd={(name) => onPutCat(matCatId(), name)} onDelete={onDelCat} />
      ) : null}

      {showImport ? (
        <CsvImport cats={cats} onClose={() => setShowImport(false)} onImport={(list) => { for (const { id, input } of list) onPutMaterial(id, input, []); setShowImport(false); }} />
      ) : null}

      {/* v1 delMaterial — typed-delete gated, exact wording */}
      {delMat ? (
        <ConfirmDelete
          message={`Delete "${delMat.name}"? This cannot be undone.`}
          onCancel={() => setDelMat(null)}
          onConfirm={() => { onDelMaterial(delMat.natKey); setDelMat(null); }}
        />
      ) : null}
    </div>
  );
}

/** v1 openMaterial add/edit modal — the full material record editor. */
/** Exported: the SOW item editor opens THIS same editor for "＋ New material",
 * so inline creation has every sidebar feature (URL import, SKU, notes, …). */
export function MaterialEditor({ init, cats, onCancel, onSave, onImportUrl }: {
  init: { id: string; input: MaterialInput; history: { date: string; price: number }[] };
  cats: MaterialCat[];
  onCancel: () => void;
  onSave: (input: MaterialInput) => void;
  onImportUrl?: (url: string, onWake: () => void) => Promise<MaterialPriceResult>;
}) {
  const [f, setF] = useState<MaterialInput>(init.input);
  const set = (k: keyof MaterialInput, v: string | number) => setF((p) => ({ ...p, [k]: v }));
  const [impUrl, setImpUrl] = useState(init.input.website || "");
  const [imping, setImping] = useState(false);
  const [impMsg, setImpMsg] = useState<{ kind: "hint" | "err"; text: string } | null>(null);
  const doImport = async () => {
    const u = impUrl.trim();
    if (!u || !onImportUrl || imping) return;
    setImping(true);
    setImpMsg(null);
    try {
      const d = await onImportUrl(u, () => setImpMsg({ kind: "hint", text: "Waking the price service — one moment…" }));
      const got = Boolean(d.title) || typeof d.price === "number" || Boolean(d.image);
      if (d.detail === "invalid url" || !got) {
        setImpMsg({ kind: "err", text: d.detail === "invalid url" ? "That link couldn't be read — check the URL." : (d.detail ?? "Nothing found at that link.") });
      } else {
        setF((p) => ({
          ...p,
          name: d.title ? d.title : p.name,
          price: typeof d.price === "number" ? d.price : p.price,
          thumbnail: d.image ? d.image : p.thumbnail,
          website: u,
        }));
        setImpMsg(typeof d.price === "number"
          ? { kind: "hint", text: d.source ? "Imported from " + d.source : "Imported — name, price and image filled in." }
          : { kind: "hint", text: "Imported the name" + (d.image ? " and image" : "") + " — the page didn't show a price, so type it in." });
      }
    } catch (e) {
      setImpMsg({ kind: "err", text: e instanceof Error ? e.message : "Import failed — try again." });
    } finally {
      setImping(false);
    }
  };
  const submit = () => {
    if (!f.name.trim()) { return; }
    if (!f.categoryId) { return; }
    onSave(f);
  };
  const ok = f.name.trim() && f.categoryId;
  return (
    <div className="cmpmodalscrim" onClick={onCancel}>
      <div className="cmpmodal wide" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead"><h3>{init.id ? "Edit material" : "Add material"}</h3><button className="close" onClick={onCancel}>×</button></div>
          <div className="matform">
            {onImportUrl ? (
              <div className="matimport">
                <div className="matimporthd"><span>Import from a product link</span><span className="sub">Amazon · Home Depot · Lowe's · Floor &amp; Decor · Walmart — fills name, price &amp; image</span></div>
                <div className="matimportrow">
                  <input value={impUrl} onChange={(e) => setImpUrl(e.target.value)} placeholder="Paste product URL…" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void doImport(); } }} />
                  <button type="button" className="btn slim primary" disabled={imping || !impUrl.trim()} onClick={() => void doImport()}>{imping ? "Importing…" : "⬇ Import"}</button>
                </div>
                {impMsg ? <div className={"matimportmsg " + impMsg.kind}>{impMsg.text}</div> : null}
                {f.thumbnail ? <div className="matimportprev"><img src={f.thumbnail} referrerPolicy="no-referrer" alt="" /></div> : null}
              </div>
            ) : null}
            <label className="wide"><span>Description *</span><input value={f.name} onChange={(e) => set("name", e.target.value)} /></label>
            <label><span>Category *</span>
              <select value={f.categoryId ?? ""} onChange={(e) => set("categoryId", e.target.value)}>
                <option value="">Select…</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label><span>Price</span><input type="number" value={f.price} onChange={(e) => set("price", Number(e.target.value) || 0)} /></label>
            <label><span>Unit</span>
              <select value={f.unit} onChange={(e) => set("unit", e.target.value)}>
                {MAT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                {f.unit && !MAT_UNITS.includes(f.unit) ? <option value={f.unit}>{f.unit}</option> : null}
              </select>
            </label>
            <label><span>Vendor</span>
              <select value={f.vendor} onChange={(e) => set("vendor", e.target.value)}>
                <option value="">—</option>
                {MAT_VENDORS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
              </select>
            </label>
            <label><span>Supplier / brand</span><input value={f.supplier} onChange={(e) => set("supplier", e.target.value)} /></label>
            <label><span>SKU</span><input value={f.sku} onChange={(e) => set("sku", e.target.value)} /></label>
            <label><span>Internet #</span><input value={f.internet} onChange={(e) => set("internet", e.target.value)} /></label>
            <label className="wide"><span>Website</span><input value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="homedepot.com/…" /></label>
            <label className="wide"><span>Thumbnail URL</span><input value={f.thumbnail} onChange={(e) => set("thumbnail", e.target.value)} /></label>
            <label className="wide"><span>Notes</span><textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></label>
          </div>
          <div className="expformacts">
            <button className="btn slim primary" disabled={!ok} onClick={submit}>{init.id ? "Update material" : "Add material"}</button>
            <button className="btn slim" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** v1 openMatCategory — add / delete categories. */
function CategoryManager({ cats, rows, onClose, onAdd, onDelete }: {
  cats: MaterialCat[]; rows: CatalogMaterial[]; onClose: () => void;
  onAdd: (name: string) => void; onDelete: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const confirmDel = useConfirmDelete(); // v1 delMatCategory — typed-delete gated
  const add = () => { const n = name.trim(); if (!n) return; onAdd(n); setName(""); };
  const count = (id: string) => rows.filter((m) => m.categoryId === id).length;
  return (
    <div className="cmpmodalscrim" onClick={onClose}>
      <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead"><h3>Categories</h3><button className="close" onClick={onClose}>×</button></div>
          <div className="relvadd">
            <input placeholder="e.g. Paint, Flooring" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
            <button className="btn" onClick={add}>Add</button>
          </div>
          <div className="matcatlist">
            {cats.length === 0 ? <div className="sub">No categories yet.</div> : null}
            {cats.map((c) => (
              <div key={c.id} className="matcatrow">
                <span>{c.name} <span className="sub">· {count(c.id)}</span></span>
                <button className="rowbtn del" title="Delete category" onClick={() => confirmDel.ask(`Delete category "${c.name}"? Materials in it will also be deleted.`, () => onDelete(c.id))}>✕</button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {confirmDel.modal}
    </div>
  );
}

/** v1 openMatImport / doMatCsvImport — paste or upload CSV, mapped by parseMatCsv. */
function CsvImport({ cats, onClose, onImport }: {
  cats: MaterialCat[]; onClose: () => void; onImport: (list: { id: string; input: MaterialInput }[]) => void;
}) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<{ ok: number; errors: string[] } | null>(null);
  const preview = () => {
    const { materials, errors } = parseMatCsv(text, cats);
    setResult({ ok: materials.length, errors });
  };
  const doImport = () => {
    const { materials } = parseMatCsv(text, cats);
    if (materials.length) onImport(materials);
  };
  return (
    <div className="cmpmodalscrim" onClick={onClose}>
      <div className="cmpmodal wide" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead"><h3>Import materials from CSV</h3><button className="close" onClick={onClose}>×</button></div>
          <p className="sub" style={{ marginBottom: 8 }}>Header row required. Columns: <code>name, category</code> (required) · price, supplier, sku, unit, website_url, notes, internet_number. Category must match an existing category name.</p>
          <textarea className="notes" rows={7} value={text} placeholder={"name,category,price,supplier,sku,unit\nPaint - Interior White,Paint,25.99,Behr,PPG-INT-WHT,gallon"} onChange={(e) => { setText(e.target.value); setResult(null); }} />
          {result ? (
            <div className="matimportres">
              <span className="good">{result.ok} ready</span>
              {result.errors.length ? <span className="bad">{result.errors.length} skipped</span> : null}
              {result.errors.length ? <div className="matimporterrs">{result.errors.slice(0, 8).map((e, i) => <div key={i}>{e}</div>)}</div> : null}
            </div>
          ) : null}
          <div className="expformacts">
            <button className="btn slim" onClick={preview} disabled={!text.trim()}>Preview</button>
            <button className="btn slim primary" onClick={doImport} disabled={!text.trim()}>Import</button>
            <button className="btn slim" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
